import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon, MailIcon } from 'lucide-react';
import { partners, teamMembers } from '../data/partners';
import { TeamMemberCard } from './TeamMemberCard';
import { BrandMark } from './BrandMark';
import { Button } from './Button';
import { PartnerPhoto } from './PartnerPhoto';
import { Reveal } from './Reveal';
const ease = [0.23, 1, 0.32, 1] as const;
export function PartnersSection() {
  const reduce = useReducedMotion();
  return <section id="partners" aria-labelledby="partners-heading" className="scroll-mt-20 bg-white pb-24 lg:pb-32" data-id="element-250" data-mp="34oppl488lt3c">
      <div className="mx-auto grid max-w-7xl gap-12 border-t border-ink/10 px-6 pt-24 lg:grid-cols-12 lg:gap-12 lg:px-10 lg:pt-32" data-id="element-251" data-mp="3ilcnpabxepau">
        <Reveal className="lg:col-span-4" data-id="element-252" data-mp="xwyfr5zysrdh">
          <BrandMark size={36} className="text-navy" data-id="element-253" data-mp="xvnaf1qvfbk9" />
          <h2 id="partners-heading" className="mt-6 font-serif text-4xl font-medium leading-[1.1] text-navy sm:text-5xl" data-id="element-254" data-mp="sydmjqj4r7yj">
            Meet Your Search Partners
          </h2>
          <p className="mt-6 max-w-sm text-lg leading-relaxed text-ink/75" data-id="element-255" data-mp="1o8i4gmc4ahi0">
            The people who lead each search and serve as your point of contact throughout.
          </p>
        </Reveal>

        <ul className="grid gap-14 sm:grid-cols-2 sm:gap-10 lg:col-span-8" data-id="element-256" data-mp="1y7qwvarc3o29">
          {partners.map((p, i) => {
          const delay = i * 0.08;
          return <li key={p.email} className="flex flex-col" data-id="element-257" data-mp="15ijnfgqcde28" data-mpi={i}>
                <PartnerPhoto name={p.name} initials={p.initials} image={p.image} imageAlt={p.imageAlt} imagePosition={p.imagePosition} imageZoom={p.imageZoom} delay={delay} data-id="element-258" data-mp="15j364zntg6hb" />

                <motion.div className="flex flex-1 flex-col pt-7" initial={reduce ? false : {
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '0px 0px -60px 0px'
            }} transition={{
              duration: 0.3,
              delay: delay + 0.12,
              ease
            }} data-id="element-259" data-mp="hqv2wmjrhtvu">
                  <h3 className="font-serif text-[28px] font-medium leading-tight text-navy" data-id="element-260" data-mp="ofuoymme7w73">
                    {p.name}
                  </h3>
                  <p className="mt-1.5 text-[15px] text-ink/70" data-id="element-261" data-mp="3rxwdy8jzrnkh">{p.title}</p>
                  <span className="mt-5 block h-px w-10 bg-gold" aria-hidden="true" data-id="element-262" data-mp="3rzz0ujpul0kz" />

                  <a href={`mailto:${p.email}`} className="mt-5 inline-flex items-center gap-2 self-start break-all rounded-sm text-[15px] font-medium text-navy underline decoration-gold decoration-1 underline-offset-4 transition-colors duration-150 ease-out hover:text-gold-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-263" data-mp="3nxvqjyex021a">
                    <MailIcon size={16} strokeWidth={1.5} className="shrink-0" aria-hidden="true" data-id="element-264" data-mp="1c8upqg3luglh" />
                    {p.email}
                  </a>

                  {p.bio && <p className="mt-5 leading-relaxed text-ink/75" data-id="element-265" data-mp="2z4j133jqczur">{p.bio}</p>}

                  <div className="mt-auto flex flex-wrap gap-3 pt-6" data-id="element-266" data-mp="2l4891x6opf80">
                    <Button href={`mailto:${p.email}`} size="sm" ariaLabel={`Contact ${p.firstName} by email at ${p.email}`} data-id="element-267" data-mp="2l61gvft1a5hl">
                      Contact {p.firstName}
                    </Button>
                    {p.linkedinUrl && <Button href={p.linkedinUrl} external size="sm" variant="outline" ariaLabel={`View ${p.name}’s LinkedIn profile (opens in a new tab)`} data-id="element-268" data-mp="3432s3id6l1tz">
                        View LinkedIn Profile
                        <ArrowUpRightIcon size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-269" data-mp="5dvk6ds5fgm4" />
                      </Button>}
                  </div>
                </motion.div>
              </li>;
        })}
        </ul>

        <div className="lg:col-span-12" data-id="element-270" data-mp="23uxn7hbwj2q7">
          <Reveal className="mt-4 flex items-center gap-4 lg:mt-8" data-id="element-271" data-mp="23vz78bawazk2">
            <h3 className="font-serif text-3xl font-medium text-navy" data-id="element-272" data-mp="1yx4eagj7rum8">Executive Search Team</h3>
            <span className="h-px flex-1 bg-ink/10" aria-hidden="true" data-id="element-273" data-mp="2vmvcapqprzws" />
          </Reveal>
          <ul className="mt-8 grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-12" data-id="element-274" data-mp="2rki9oa79ki48">
            {teamMembers.map((m, i) => <li key={m.email} data-id="element-275" data-mp="2mt0fq63nrhsr" data-mpi={i}>
                <Reveal delay={i * 0.05} className="h-full" data-id="element-276" data-mp="2mrysl39db4uo">
                  <TeamMemberCard {...m} data-id="element-277" data-mp="2hufhgc7yv86y" />
                </Reveal>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}